import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer.js";
import { useAuth } from "@/auth/useAuth.js";
import {
  COMPLIANCE_LABEL, APE_CLASSES,
  useListingCompliance, usePatchComplianceItem,
  type ComplianceItem, type ComplianceItemKey, type ComplianceItemPatch,
} from "@/hooks/useCompliance.js";

const BLOCKING: ComplianceItemKey[] = ["incarico", "ape", "provvigione"];

const localDay = (d: Date) => new Intl.DateTimeFormat("en-CA").format(d);

function expiryTone(expiresOn: string | null): string {
  if (!expiresOn) return "text-ink-mute";
  if (expiresOn < localDay(new Date())) return "text-risk";
  if (expiresOn <= localDay(new Date(Date.now() + 30 * 86_400_000))) return "text-warn";
  return "text-ink-mute";
}

export function ComplianceDrawer({ listingId, listingLabel, onClose }: {
  listingId: string | null;
  listingLabel?: string;
  onClose: () => void;
}): JSX.Element {
  const { role } = useAuth();
  const canEdit = role === "owner" || role === "admin" || role === "editor";
  const { data, isLoading, error } = useListingCompliance(listingId);
  const patch = usePatchComplianceItem();

  const save = (item: ComplianceItemKey, input: ComplianceItemPatch) => {
    if (!listingId) return;
    patch.mutate({ listingId, item, ...input }, {
      onError: (e) => toast.error(e instanceof Error ? e.message : "Salvataggio non riuscito"),
    });
  };

  return (
    <Drawer open={!!listingId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent className="flex flex-col">
        <div className="border-b border-rule p-5">
          <DrawerTitle className="text-[15px] font-semibold text-ink">Adempimenti</DrawerTitle>
          <DrawerDescription className="mt-1 truncate text-[12.5px] text-ink-soft">
            {listingLabel ?? "Checklist immobile"}
          </DrawerDescription>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <p className="text-[13px] text-ink-mute">Carico…</p>
          ) : error ? (
            <p className="rounded-[5px] border border-risk/30 bg-risk/10 p-3 text-[13px] text-risk">
              Errore nel caricamento: {(error as Error).message}
            </p>
          ) : (
            <div className="space-y-1">
              {(data?.items ?? []).map((row) => (
                <ItemRow key={row.item} row={row} canEdit={canEdit} onSave={save} />
              ))}
            </div>
          )}
          <p className="mt-5 text-[11.5px] leading-relaxed text-ink-mute">
            Le voci bloccanti (incarico, APE, provvigione) accendono il badge rosso in lista finché mancano o risultano scadute.
          </p>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function ItemRow({ row, canEdit, onSave }: {
  row: ComplianceItem;
  canEdit: boolean;
  onSave: (item: ComplianceItemKey, input: ComplianceItemPatch) => void;
}): JSX.Element {
  const done = row.status === "fatto";
  const blocking = BLOCKING.includes(row.item);
  const hasExpiry = row.item === "incarico" || row.item === "ape";
  // La data si salva al blur, non a ogni tasto: digitare "2026" pezzo per
  // pezzo non deve produrre PATCH intermedi.
  const [expiryDraft, setExpiryDraft] = useState(row.expires_on ?? "");
  useEffect(() => { setExpiryDraft(row.expires_on ?? ""); }, [row.expires_on]);
  const commitExpiry = () => {
    const next = expiryDraft || null;
    if (next !== (row.expires_on ?? null)) onSave(row.item, { expires_on: next });
  };

  return (
    <div className="border-b border-rule-soft py-[14px] last:border-b-0">
      <div className="flex items-center gap-2">
        {done ? <ShieldCheck className="size-4 shrink-0 text-success" /> : <ShieldAlert className={`size-4 shrink-0 ${blocking ? "text-risk" : "text-ink-mute"}`} />}
        <span className="text-[13.5px] font-semibold text-ink">{COMPLIANCE_LABEL[row.item]}</span>
        {blocking ? <span className="micro text-ink-mute">bloccante</span> : null}
        <div className="ml-auto flex gap-[6px]">
          <StatusPill label="Manca" active={!done} tone="risk" disabled={!canEdit} onClick={() => onSave(row.item, { status: "manca" })} />
          <StatusPill label="Fatto" active={done} tone="success" disabled={!canEdit} onClick={() => onSave(row.item, { status: "fatto" })} />
        </div>
      </div>
      {(hasExpiry || row.item === "ape" || row.item === "incarico") ? (
        <div className="mt-[10px] flex flex-wrap items-center gap-[10px] pl-6">
          {hasExpiry ? (
            <label className="flex items-center gap-[7px] text-[12px] text-ink-soft">
              Scade il
              <input
                type="date"
                value={expiryDraft}
                disabled={!canEdit}
                onChange={(e) => setExpiryDraft(e.target.value)}
                onBlur={commitExpiry}
                onKeyDown={(e) => { if (e.key === "Enter") commitExpiry(); }}
                className={`h-[28px] rounded-[5px] border border-rule bg-surface-2 px-2 font-mono text-[12px] outline-none focus:border-accent ${expiryTone(row.expires_on)}`}
              />
            </label>
          ) : null}
          {row.item === "ape" ? (
            <label className="flex items-center gap-[7px] text-[12px] text-ink-soft">
              Classe
              <select
                value={row.ape_class ?? ""}
                disabled={!canEdit}
                onChange={(e) => onSave(row.item, { ape_class: e.target.value || null })}
                className="h-[28px] cursor-pointer rounded-[5px] border border-rule bg-surface-2 px-2 font-mono text-[12px] text-ink outline-none [&>option]:bg-surface [&>option]:text-ink"
              >
                <option value="">—</option>
                {APE_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          ) : null}
          {row.item === "incarico" ? (
            <span className="flex items-center gap-[7px] text-[12px] text-ink-soft">
              Esclusiva
              <StatusPill label="Sì" active={row.esclusiva === true} tone="accent" disabled={!canEdit} onClick={() => onSave(row.item, { esclusiva: true })} />
              <StatusPill label="No" active={row.esclusiva === false} tone="accent" disabled={!canEdit} onClick={() => onSave(row.item, { esclusiva: false })} />
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StatusPill({ label, active, tone, disabled, onClick }: {
  label: string;
  active: boolean;
  tone: "risk" | "success" | "accent";
  disabled?: boolean;
  onClick: () => void;
}): JSX.Element {
  const activeCls = {
    risk: "border-risk/50 bg-risk/10 text-risk",
    success: "border-success/50 bg-success/10 text-success",
    accent: "border-accent bg-accent/10 text-accent",
  }[tone];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`h-[24px] rounded-full border px-[10px] text-[11.5px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${active ? activeCls : "border-rule bg-surface-2 text-ink-soft hover:border-ink-mute hover:text-ink"}`}
    >
      {label}
    </button>
  );
}
